import { MessageFilled } from "@ant-design/icons";
import { Empty, Flex } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { getContractListThunk } from "tm-shared/api";
import {
    Contract,
    Market,
    TaskDMP as TaskDMPType,
    TaskRoute,
    TaskStatus,
} from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";

import { BottomSheet } from "@/components/BottomSheet";
import { StatusCycleButton } from "@/components/CycleButton";
import { GalleryUploader } from "@/components/GalleryUploader";
import Header from "@/components/Header";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { SVScanButton } from "@/components/SVScanButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useActiveScrollNavigator } from "@/hooks/useActiveScrollNavigator";
import { useCheckCommentOnReject } from "@/hooks/useCheckCommentOnReject";
import { useMarketDistance } from "@/hooks/useMarketDistance";
import { useStickyFilteredItems } from "@/hooks/useStickyFilteredItems";
import { useTaskCompletion } from "@/hooks/useTaskCompletion";
import { useTaskConfigSelector } from "@/hooks/useTaskSelectors";
import { mutateDMPPhotos, patchDMPEntity } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { DMPController } from "./components/DMPController";
import { TaskDMP } from "./components/TaskDMP";
import { TaskFinish } from "./components/TaskFinish";
import {
    StyledBottomSheetTop,
    StyledList,
    StyledMainContainer,
} from "./Styled";

interface DMPPageProps {
    market: Market;
    task: TaskDMPType;
}

export const DMPPage: FC<DMPPageProps> = ({ task, market }) => {
    const dispatch = useAppDispatch();

    const marketId = market.id;
    const taskId = task.id;

    const { firstName, lastName } = useAppSelector((s) => s.user);
    const { targetsContracts } = useAppSelector((state) => state.offline);
    const { dmp } = useAppSelector((state) => state.progress);

    const allContracts = useMemo(() => {
        if (!targetsContracts) return [];

        const contractSources = [
            marketId,
            market.storage?.id,
            market.contragent?.id,
            market.address?.mainRegionId,
            market.networkId,
        ].filter(Boolean);

        const contracts: Contract[] = [];
        const seenIds = new Set<string>();

        contractSources.forEach((sourceId) => {
            const sourceContracts = targetsContracts[sourceId!];
            if (sourceContracts) {
                Object.values(sourceContracts).forEach((contract) => {
                    if (contract && !seenIds.has(contract.id)) {
                        contracts.push(contract);
                        seenIds.add(contract.id);
                    }
                });
            }
        });

        return contracts;
    }, [market, targetsContracts, marketId]);

    const dmpPayloadMap = useMemo(() => dmp[taskId] ?? {}, [dmp, taskId]);
    const { activeItem, showUncompleted } = useTaskConfigSelector({
        marketId,
        taskId,
    });
    const activeDMPPayload = dmpPayloadMap?.[activeItem];

    const activeStatus = activeDMPPayload?.status ?? TaskStatus.Incomplete;

    const dmpSubtasks = task.context.dmp;

    const prevDMPIdRef = useRef<string | null>(null);

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                value: null,
            }),
        );
    }, [dispatch, marketId, taskId]);

    const onChangeActive = useCallback(
        (id: string) => {
            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    value: id,
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (nextId: string) => {
            const currentId = prevDMPIdRef.current;
            const currentPayload = dmpPayloadMap?.[currentId];

            if (validateAndNotify(currentPayload)) {
                return;
            }

            onChangeActive(nextId);
        },
        [dmpPayloadMap, onChangeActive, validateAndNotify],
    );

    const { filteredItems: filteredTasks } = useStickyFilteredItems({
        items: dmpSubtasks,
        showUncompleted: showUncompleted ?? false,
        getItemId: (dmp) => dmp.subtaskId,
        payloadsMap: dmpPayloadMap ?? {},
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const { activeIndex, getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredTasks.map((t) => t.subtaskId),
            activeId: activeItem,
            onChangeActive: handleCheckCommentOnReject,
        });

    const activeTaskName = task?.context?.dmp?.[activeIndex]?.name;
    const activeTaskContractId = task?.context?.dmp?.[activeIndex]?.contractId;

    const onClickInfoButton = useCallback(() => {
        const currentContract = allContracts.find(
            (contract) => contract.id === activeTaskContractId,
        );

        dispatch(
            addModalToQueue({
                type: ModalType.TaskInfo,
                id: ModalType.TaskInfo,
                payload: {
                    title: `${currentContract?.name} ( ${formatUAHStandard(
                        currentContract?.context?.placePrice,
                    )})`,
                    description: currentContract?.description,
                    files: currentContract?.files,
                },
            }),
        );
    }, [activeTaskContractId, allContracts, dispatch]);

    const resetStatusIfNeeded = useCallback(
        (dmpId: string) => {
            const payload = dmpPayloadMap?.[dmpId];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchDMPEntity({
                        taskId,
                        contractId: activeTaskContractId,
                        dmpId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [dmpPayloadMap, dispatch, taskId, activeTaskContractId],
    );

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, id?: string) => {
            const targetId = id ?? activeItem;

            if (!targetId) return;

            dispatch(
                patchDMPEntity({
                    taskId,
                    contractId: activeTaskContractId,
                    dmpId: targetId,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [activeItem, dispatch, taskId, activeTaskContractId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activeItem) return;

            dispatch(
                mutateDMPPhotos({
                    taskId,
                    contractId: activeTaskContractId,
                    dmpId: activeItem,
                    option: "add",
                    photo,
                }),
            );
        },
        [activeItem, dispatch, taskId, activeTaskContractId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activeItem) return;

            dispatch(
                mutateDMPPhotos({
                    taskId,
                    dmpId: activeItem,
                    contractId: activeTaskContractId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [activeItem, dispatch, taskId, activeTaskContractId],
    );

    const handlerMessage = useCallback(() => {
        if (activeStatus === TaskStatus.Incomplete || !activeItem) return;

        dispatch(
            addModalToQueue({
                type: ModalType.Comment,
                id: ModalType.Comment,
                payload: {
                    initialComment: activeDMPPayload?.comment ?? "",
                    route: TaskRoute.Dmp,
                    taskId,
                    contractId: activeTaskContractId,
                    dmpId: activeItem,
                    taskStatus: activeStatus,
                },
            }),
        );
    }, [
        activeStatus,
        activeItem,
        dispatch,
        activeDMPPayload?.comment,
        taskId,
        activeTaskContractId,
    ]);

    useEffect(() => {
        const prev = prevDMPIdRef.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevDMPIdRef.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    const { isAllCompleted } = useTaskCompletion({
        payloadMap: dmpPayloadMap,
        expectedCount: dmpSubtasks.length,
    });

    const { distance } = useMarketDistance({ marketGeo: market.geo });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                marketName: market.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, market.name, task.name],
    );

    useEffect(() => {
        if (allContracts?.length) return;

        const loadContracts = async () => {
            try {
                await Promise.all([
                    dispatch(
                        getContractListThunk({
                            marketId,
                            isActive: true,
                        }),
                    ),
                ]);
            } catch (error) {
                console.error("Error loading contracts:", error);
            }
        };

        loadContracts();
    }, [allContracts?.length, dispatch, marketId]);

    useEffect(() => {
        scrollToActive();
    }, [isAllCompleted]);

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <>
                        <SVScanButton marketId={marketId} taskId={taskId} />
                    </>
                }
            />

            <DMPController task={task} market={market} />

            <StyledMainContainer>
                <StyledList>
                    {filteredTasks.length > 0 ? (
                        filteredTasks.map((dmp) => {
                            const { subtaskId } = dmp;
                            const currentDMPPayload =
                                dmpPayloadMap?.[subtaskId];
                            const isActive = subtaskId === activeItem;

                            return (
                                <div
                                    key={subtaskId}
                                    ref={getItemRef(subtaskId)}
                                >
                                    <TaskDMP
                                        dmp={dmp}
                                        isActive={isActive}
                                        onChangeActive={onChangeActive}
                                        payload={currentDMPPayload}
                                        onChangeTaskStatus={onChangeTaskStatus}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <Empty
                            style={{ marginTop: 32 }}
                            description={"Задач не знайдено"}
                        />
                    )}
                </StyledList>

                <TaskFinish
                    isAllCompleted={isAllCompleted}
                    dmpPayloadMap={dmpPayloadMap}
                    marketId={marketId}
                    taskId={taskId}
                />
            </StyledMainContainer>

            <BottomSheet
                open={!!activeItem}
                onClose={onCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={onClickInfoButton}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>{activeTaskName}</PrimaryText>
                    <Flex gap="20px">
                        <OutlineIconButton
                            icon={<MessageFilled />}
                            onClick={handlerMessage}
                            disabled={activeStatus === TaskStatus.Incomplete}
                        />

                        <StatusCycleButton
                            value={activeStatus}
                            onChange={onChangeTaskStatus}
                            states={[
                                TaskStatus.Incomplete,
                                TaskStatus.Rejected,
                            ]}
                            disabled={activeStatus === TaskStatus.Completed}
                        />
                    </Flex>
                </StyledBottomSheetTop>

                <GalleryUploader
                    previewTitle={activeTaskName}
                    onDelete={onDeletePhoto}
                    onUpload={onUploadPhoto}
                    activeItemId={activeItem}
                    marketId={marketId}
                    savedPhotos={activeDMPPayload?.photos ?? []}
                    disabled={activeStatus === TaskStatus.Rejected}
                    captionLines={captions}
                />
            </BottomSheet>
        </>
    );
};
